
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActivation } from '@/contexts/ActivationContext';
import { getEndpointUrl } from '@/lib/api-config';
import { TERMINOS_PDF_FILENAME, TERMINOS_PDF_PATH } from '@/lib/terminos';
import ImageSlideshow from '@/components/ImageSlideshow';

import img1 from '@/assets/1-1778274698.webp';
import img2 from '@/assets/2-1778274698.webp';
import img3 from '@/assets/3-1778274698.webp';
import img4 from '@/assets/4-1778274698.webp';
import img5 from '@/assets/5-1778274698.webp';
import img6 from '@/assets/6-1778274698.webp';
import img7 from '@/assets/7-1778274698.webp';
import img8 from '@/assets/8-1778274698.webp';

import hd1 from '@/assets/hd/1-1778274698.webp';
import hd2 from '@/assets/hd/2-1778274698.webp';
import hd3 from '@/assets/hd/3-1778274698.webp';
import hd4 from '@/assets/hd/4-1778274698.webp';
import hd5 from '@/assets/hd/5-1778274698.webp';
import hd6 from '@/assets/hd/6-1778274698.webp';
import hd7 from '@/assets/hd/7-1778274698.webp';
import hd8 from '@/assets/hd/8-1778274698.webp';

const SLIDESHOW_IMAGES = [
  { src: img1, alt: 'Información FLUX 1' },
  { src: img2, alt: 'Información FLUX 2' },
  { src: img3, alt: 'Información FLUX 3' },
  { src: img4, alt: 'Información FLUX 4' },
  { src: img5, alt: 'Información FLUX 5' },
  { src: img6, alt: 'Información FLUX 6' },
  { src: img7, alt: 'Información FLUX 7' },
  { src: img8, alt: 'Información FLUX 8' },
];

const SLIDESHOW_HD_IMAGES = [
  { src: hd1 },
  { src: hd2 },
  { src: hd3 },
  { src: hd4 },
  { src: hd5 },
  { src: hd6 },
  { src: hd7 },
  { src: hd8 },
];

const formatDate = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Santiago',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function TerminosPanel() {
  const navigate = useNavigate();
  const { isStepComplete, status, refreshStatus } = useActivation();
  const isCompleted = isStepComplete('terminos');
  const completedAt = status?.terminos?.completedAt ?? null;

  const [checked, setChecked] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    if (!checked) return;
    setError('');
    setAccepting(true);
    try {
      const url = getEndpointUrl('terminos', 'accept');
      const { httpClient } = await import('@/lib/http-client');
      await httpClient.post(url, { acceptedAt: new Date().toISOString() });
      await refreshStatus();
      navigate('/configuraciones/resumen');
    } catch (err: any) {
      setError(err?.message || 'Error al registrar la aceptación. Intenta nuevamente.');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden min-h-[600px] flex flex-col">
      <div className="p-6 md:p-8 flex-grow flex flex-col">
        <div className="mb-6 border-b border-slate-100 dark:border-slate-800 pb-6">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            {isCompleted && (
              <span className="material-symbols-outlined text-emerald-500 text-2xl">verified</span>
            )}
            Términos y Condiciones
          </h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            {isCompleted
              ? 'Documentación legal aceptada. Puedes descargar una copia para tus registros.'
              : 'Por favor, revisa y acepta los términos de uso para continuar utilizando la plataforma.'}
          </p>
        </div>

        <ImageSlideshow images={SLIDESHOW_IMAGES} hdImages={SLIDESHOW_HD_IMAGES} className="mb-6" />

        <div className="flex-grow flex flex-col bg-slate-50 dark:bg-slate-950 rounded-lg border border-slate-200 dark:border-slate-800 mb-8">
          <div className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-2 flex justify-between items-center rounded-t-lg">
            <div className="flex items-center gap-2 px-2">
              <span className="material-symbols-outlined text-slate-400 text-base">picture_as_pdf</span>
              <span className="text-xs font-medium text-slate-500">{TERMINOS_PDF_FILENAME}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-4 w-px bg-slate-300 dark:bg-slate-600 mx-1" />
              <a
                href={TERMINOS_PDF_PATH}
                download={TERMINOS_PDF_FILENAME}
                className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-500 transition-colors"
                title="Descargar"
              >
                <span className="material-symbols-outlined text-lg">download</span>
              </a>
              <a
                href={TERMINOS_PDF_PATH}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-500 transition-colors"
                title="Abrir en nueva pestaña"
              >
                <span className="material-symbols-outlined text-lg">open_in_new</span>
              </a>
            </div>
          </div>

          <div className="flex-grow h-[500px]">
            <iframe
              src={TERMINOS_PDF_PATH}
              className="w-full h-full rounded-b-lg"
              title="Términos y Condiciones FLUX"
            />
          </div>
        </div>

        <div className="border-t border-slate-100 dark:border-slate-800 pt-6">
          {isCompleted ? (
            <div className="flex flex-col items-center py-2">
              <div className="rounded-full bg-emerald-100 dark:bg-emerald-900/30 p-3 mb-3">
                <span className="material-symbols-outlined text-3xl text-emerald-600 dark:text-emerald-400">
                  check_circle
                </span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                Has aceptado los Términos y Condiciones
              </h3>
              {completedAt && (
                <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">calendar_today</span>
                  Fecha de aceptación: {formatDate(completedAt)}
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-4">
              {error && (
                <div className="w-full max-w-2xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">error</span>
                  {error}
                </div>
              )}

              <div className="flex items-start gap-3 max-w-2xl">
                <input
                  id="accept_terms"
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  className="mt-1 rounded border-slate-300 text-primary focus:ring-primary h-4 w-4 cursor-pointer"
                />
                <label
                  htmlFor="accept_terms"
                  className="text-sm text-slate-600 dark:text-slate-400 cursor-pointer"
                >
                  Declaro haber leído íntegramente y aceptado los{' '}
                  <span className="font-semibold text-slate-900 dark:text-white">
                    Términos y Condiciones
                  </span>
                  , así como la Política de Privacidad de FLUX. Entiendo que esta aceptación tiene la
                  misma validez legal que una firma manuscrita.
                </label>
              </div>

              <div className="flex flex-col items-center w-full">
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={!checked || accepting}
                  className="inline-flex justify-center items-center px-8 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 w-full sm:w-auto min-w-[280px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {accepting ? (
                    <>
                      <span className="material-symbols-outlined text-base animate-spin mr-2">
                        progress_activity
                      </span>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined mr-2">check_circle</span>
                      Aceptar Términos y Condiciones
                    </>
                  )}
                </button>

                <p className="mt-3 text-xs font-medium text-slate-400 uppercase tracking-wide flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />
                  Pendiente de aceptación
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
